import { DBAccount } from "../../api/types";

export type OwnerGroup = {
  owner: string;
  accounts: DBAccount[];
  totalEquity: number;
};

export type AccountCategories = {
  personal: OwnerGroup[];
  testing: DBAccount[];
  disabled: DBAccount[];
};

const byAccountOrder = (a: DBAccount, b: DBAccount) =>
  (b.accountOrder ?? 0) - (a.accountOrder ?? 0);

const sumEquity = (accounts: DBAccount[]) =>
  accounts.reduce((sum, account) => sum + (account.totalEquity || 0), 0);

/**
 * Split accounts into the dashboard sections:
 *  - disabled: `active === false`, regardless of owner
 *  - personal: active and has an owner, grouped per owner
 *  - testing:  active and no owner
 */
export const categorizeAccounts = (
  accounts: DBAccount[],
): AccountCategories => {
  const disabled: DBAccount[] = [];
  const testing: DBAccount[] = [];
  const perOwner: Record<string, DBAccount[]> = {};

  for (const account of accounts) {
    const owner = account.owner?.trim();

    if (!account.active) {
      disabled.push(account);
    } else if (owner) {
      (perOwner[owner] ??= []).push(account);
    } else {
      testing.push(account);
    }
  }

  const personal = Object.entries(perOwner)
    .map(([owner, ownerAccounts]) => ({
      owner,
      accounts: ownerAccounts.sort(byAccountOrder),
      totalEquity: sumEquity(ownerAccounts),
    }))
    .sort((a, b) => a.owner.localeCompare(b.owner));

  return {
    personal,
    testing: testing.sort(byAccountOrder),
    disabled: disabled.sort(byAccountOrder),
  };
};

/** All distinct owners, for autocomplete. */
export const knownOwners = (accounts: DBAccount[]): string[] =>
  Array.from(
    new Set(
      accounts
        .map((account) => account.owner?.trim())
        .filter((owner): owner is string => !!owner),
    ),
  ).sort((a, b) => a.localeCompare(b));

/**
 * Remove a redundant "<owner>:" prefix from a name, since the owner is
 * already shown as the section header ("Dirk: ETH - RSI" -> "ETH - RSI").
 */
export const stripOwnerPrefix = (name: string, owner?: string): string => {
  const trimmedOwner = owner?.trim();
  if (!trimmedOwner) {
    return name;
  }

  const prefix = `${trimmedOwner}:`;
  if (name.toLowerCase().startsWith(prefix.toLowerCase())) {
    return name.slice(prefix.length).trim() || name;
  }

  return name;
};

export type ExchangeName = "Hyperliquid" | "OKX" | "Propr";

export const accountExchange = (
  account: DBAccount,
): ExchangeName | undefined => {
  if (account.proprAccountId) return "Propr";
  if (account.okxKey) return "OKX";
  if (account.hyperliquidAddress) return "Hyperliquid";
  return undefined;
};
